import { REMOTION_SERVICE_URL } from '../config/api';

/** Media file extensions that mean "this URL is a video". */
export const VIDEO_EXT = /\.(mp4|mov|webm|m4v)(\?|$)/i;

export interface PublishedMedia {
  url: string;
  kind: 'image' | 'video';
}

/** True when the slide carries an actual render output (vs raw media). */
export const slideHasRender = (slide: any): boolean =>
  !!(slide && (slide.rendered_video_url || slide.rendered_overlay_url));

/** The media that actually went out with the publish. Precedence: RENDERED
 * slide media (real carousels/video series) → the item's rendered output →
 * raw slide media → raw item media. Raw slide media must NEVER beat an
 * item-level render: the card synthesizes editor slides from the raw media
 * strip (no DB rows), and those showed the wrong raw video over the
 * published render (octakitty 4096, 2026-08-26). Raw is only the truth
 * when nothing was rendered at all. */
export const resolvePublishedMedia = (item: any): PublishedMedia | null => {
  const slide = Array.isArray(item.slides) && item.slides.length > 0 ? item.slides[0] : null;
  if (slide && slideHasRender(slide)) return resolveSlideMedia(slide);
  if (item.rendered_overlay_url) {
    // Despite the name, the render pipeline stores the rendered VIDEO here
    // for video items (34 mp4s in prod, 2026-08-24) — an mp4 in an <img>
    // renders as the broken-image icon, so classify by extension.
    return {
      url: item.rendered_overlay_url,
      kind: VIDEO_EXT.test(item.rendered_overlay_url) ? 'video' : 'image',
    };
  }
  const fromSlideRaw = slide ? resolveSlideMedia(slide) : null;
  if (fromSlideRaw) return fromSlideRaw;
  const mediaUrls: string[] = Array.isArray(item.media_urls) ? item.media_urls : [];
  const publishedVideo = mediaUrls.find((url) => VIDEO_EXT.test(url));
  if (publishedVideo) return { url: publishedVideo, kind: 'video' };
  if (item.primary_image_url) return { url: item.primary_image_url, kind: 'image' };
  if (mediaUrls[0]) return { url: mediaUrls[0], kind: 'image' };
  return null;
};

/** Per-slide flavor of the same precedence: the rendered video beats the
 * rendered overlay beats the raw slide media. */
export const resolveSlideMedia = (slide: any): PublishedMedia | null => {
  if (!slide) return null;
  if (slide.rendered_video_url) return { url: slide.rendered_video_url, kind: 'video' };
  if (slide.rendered_overlay_url) {
    // Same dual-use column as the item-level field — classify by extension.
    return {
      url: slide.rendered_overlay_url,
      kind: VIDEO_EXT.test(slide.rendered_overlay_url) ? 'video' : 'image',
    };
  }
  if (slide.media_url) {
    return {
      url: slide.media_url,
      kind: slide.media_type === 'video' || VIDEO_EXT.test(slide.media_url) ? 'video' : 'image',
    };
  }
  return null;
};

/** Rendered-media URLs can be service-relative (card precedent). */
export const absMediaUrl = (url: string): string =>
  url.startsWith('http') ? url : `${REMOTION_SERVICE_URL}${url}`;
